import { LogoutIcon, StarIcon as StarOutlineIcon } from "@heroicons/react/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/solid";
import { Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { Link, useLocation } from "react-router-dom";
import { ZendeskAPI } from "react-zendesk";
import { MdDragIndicator } from "react-icons/md";
import { ReactSortable } from "react-sortablejs";
import { getIsAdminOfOrganization } from "src/features/profile/state/profileSlice";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import { getLegals, logoutAuth } from "../../../features/auth/state/authSlice";
import { openStandardModal } from "../../state/slice/modal/modalSlice";
import { setSelectedProjectId } from "../../../features/stats/state/statsSlice";
import { currentUserSelector } from "../../state/selectors/authSelector";
import type { RootState } from "../../state/store";
import AIChatButton from "../AIChat/AIChatButton";
import DeepLinkModal from "src/pages/Projects/DeepLinkModal";
import { SidebarNavRow } from "./SidebarItem";
import SidebarCreateMenu from "./SidebarCreateMenu";
import { SidebarSection } from "./SidebarSection";
import type { SidebarItemDef, SidebarItemId, SidebarRole } from "./sidebarConfig";
import { getSidebarItemsForRole, SIDEBAR_SECTIONS } from "./sidebarConfig";
import { useSidebarCollapse } from "./useSidebarCollapse";
import { useSidebarFavorites } from "./useSidebarFavorites";
import { SIDEBAR_FAVORITES_DND_HELP_ID, useSidebarDragAndDrop } from "./useSidebarDragAndDrop";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { hostedTourAnchors } from "src/features/onboarding/engine/tourViewAnchors";

export const LEGALS_SECTION_ID = "legalsSectionComponent";

interface IZendeskUserEvent {
  action: string;
}

const Sidebar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");

  const { user, isAdmin, proabonoWixarAiAccess, legals } = useAppSelector(
    (state: RootState) => ({
      user: currentUserSelector(state),
      isAdmin: getIsAdminOfOrganization(state),
      proabonoWixarAiAccess: getSubscriptionFeature(state, "wixar-ai-access"),
      legals: getLegals(state.auth),
    }),
    shallowEqual,
  );

  const userRole: SidebarRole = isAdmin
    ? "admin"
    : user?.role.name === "Creator"
    ? "creator"
    : "enduser";

  const hasWixarAiSubscription = proabonoWixarAiAccess?.IsEnabled;
  const canUseWixarAi = hasWixarAiSubscription && userRole !== "enduser";
  const isBetaVersion = Boolean(Number(process.env.REACT_APP_IS_BETA_VERSION ?? 0));
  const [isChatOpen, setIsChatOpen] = useState(false);

  const chatClickHandler = () => {
    if (isChatOpen) {
      ZendeskAPI("webWidget", "hide");
      setIsChatOpen(false);
    } else {
      ZendeskAPI("webWidget", "show");
      ZendeskAPI("webWidget", "open");
      setIsChatOpen(true);
    }
  };

  if (isChatOpen) {
    ZendeskAPI("webWidget:on", "userEvent", function (userEvent: IZendeskUserEvent) {
      if (userEvent.action === "Web Widget Minimised") {
        chatClickHandler();
      }
    });
  }

  const logout = () => {
    setIsChatOpen(false);
    dispatch(logoutAuth());
  };

  const items = getSidebarItemsForRole(userRole);
  const favorites = useSidebarFavorites(items);
  const { favoriteItems, isFavorite, toggleFavorite } = favorites;
  const getItemTitle = (id: SidebarItemId) => {
    const item = items.find((candidate) => candidate.id === id);
    return item ? t(item.titleKey) : id;
  };
  const [isHintDetailsOpen, setIsHintDetailsOpen] = useState(false);
  const expandFavoritesRef = useRef<() => void>();
  const dnd = useSidebarDragAndDrop(
    {
      ...favorites,
      addFavoriteAt: (id, position) => {
        favorites.addFavoriteAt(id, position);
        expandFavoritesRef.current?.();
      },
    },
    getItemTitle,
  );
  const { isCollapsed, toggle, expand } = useSidebarCollapse(items, dnd.dragForcedSection);
  expandFavoritesRef.current = () => expand("favorites");

  useTourView(
    "app-sidebar",
    true,
    hostedTourAnchors(
      "app-sidebar",
      items
        .filter((item) => !isCollapsed(item.section))
        .flatMap((item) => (item.dataTour ? [item.dataTour] : [])),
    ),
  );

  const isRouteSelected = (routePath: string) =>
    routePath.startsWith("/profile/")
      ? location.pathname === routePath
      : location.pathname === routePath || path[1] === routePath.replace("/", "");

  const renderStar = (item: SidebarItemDef, title: string) => {
    if (!item.favoritable) return undefined;
    const isFav = isFavorite(item.id);
    return (
      <button
        type="button"
        aria-pressed={isFav}
        aria-label={t(isFav ? "sidebar.favorites.remove" : "sidebar.favorites.add", {
          name: title,
        })}
        onClick={() => {
          if (!isFav) expand("favorites");
          toggleFavorite(item.id);
        }}
        data-sidebar-no-drag=""
        className={`shrink-0 p-0.5 mr-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
          isFav
            ? ""
            : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100"
        }`}
      >
        {isFav ? (
          <StarSolidIcon className="w-3 h-3 text-yellow-500" />
        ) : (
          <StarOutlineIcon className="w-3 h-3 stroke-gray-400 group-hover:stroke-white" />
        )}
      </button>
    );
  };

  const renderGrip = (item: SidebarItemDef, title: string) => (
    <button
      type="button"
      ref={dnd.registerGrip(item.id)}
      data-sidebar-no-drag=""
      aria-label={t("sidebar.favorites.reorderHandle", { name: title })}
      aria-describedby={SIDEBAR_FAVORITES_DND_HELP_ID}
      onKeyDown={(event) => dnd.handleGripKeyDown(item.id, event)}
      className="sr-only focus:not-sr-only focus:shrink-0 focus:p-0.5 focus:rounded focus:outline-none focus:ring-2 focus:ring-white"
    >
      <MdDragIndicator className="w-3 h-3 text-gray-400" />
    </button>
  );

  const renderItem = (item: SidebarItemDef, inFavorites = false) => {
    const title = t(item.titleKey);
    const dataTour = inFavorites ? undefined : item.dataTour;
    const accessory = renderStar(item, title);
    const dragProps = {
      itemId: item.id,
      favoritable: item.favoritable,
      grip: inFavorites ? renderGrip(item, title) : undefined,
    };
    if (item.action === "studio") {
      return (
        <SidebarNavRow
          key={item.id}
          icon={item.icon}
          title={title}
          dataTour={dataTour}
          accessory={accessory}
          {...dragProps}
          onClick={() => dispatch(openStandardModal("modalDeepLink"))}
        />
      );
    }
    if (item.action === "create") {
      return (
        <div key={item.id} data-sidebar-no-drag="">
          <SidebarCreateMenu title={title} canUseWixarAi={Boolean(canUseWixarAi)} />
        </div>
      );
    }
    const routePath = item.routePath ?? "/";
    return (
      <SidebarNavRow
        key={item.id}
        icon={item.icon}
        title={title}
        to={routePath}
        isSelected={isRouteSelected(routePath)}
        dataTour={dataTour}
        accessory={accessory}
        {...dragProps}
        onClick={() => dispatch(setSelectedProjectId(0))}
      />
    );
  };

  const renderSections = (bottom: boolean) =>
    SIDEBAR_SECTIONS.filter((section) => section.bottom === bottom).map((section) => {
      if (section.id === "favorites") {
        return (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={t(section.titleKey)}
            collapsed={isCollapsed(section.id)}
            onToggle={() => toggle(section.id)}
          >
            <ReactSortable key={dnd.animationKey} {...dnd.favoritesListProps(favoriteItems)}>
              {favoriteItems.length > 0 ? (
                favoriteItems.map((item) => renderItem(item, true))
              ) : (
                <Tooltip
                  title={t("sidebar.favorites.hintDetails")}
                  placement="right"
                  arrow
                  describeChild
                  open={isHintDetailsOpen}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                >
                  <button
                    type="button"
                    data-sidebar-no-drag=""
                    onClick={() => setIsHintDetailsOpen((open) => !open)}
                    onBlur={() => setIsHintDetailsOpen(false)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setIsHintDetailsOpen(false);
                    }}
                    className="block w-full text-left text-gray-400 hover:text-white text-xs italic px-2 py-px truncate"
                  >
                    {t("sidebar.favorites.hint")}
                  </button>
                </Tooltip>
              )}
            </ReactSortable>
          </SidebarSection>
        );
      }
      const sectionItems = items.filter((item) => item.section === section.id);
      if (sectionItems.length === 0) return null;
      const fixedItems = sectionItems.filter((item) => item.action === "create");
      const draggableItems = sectionItems.filter((item) => item.action !== "create");
      return (
        <SidebarSection
          key={section.id}
          id={section.id}
          title={t(section.titleKey)}
          collapsed={isCollapsed(section.id)}
          onToggle={() => toggle(section.id)}
        >
          {fixedItems.map((item) => renderItem(item))}
          <ReactSortable
            key={dnd.animationKey}
            {...dnd.sectionListProps(section.id, draggableItems)}
          >
            {draggableItems.map((item) => renderItem(item))}
          </ReactSortable>
        </SidebarSection>
      );
    });

  return (
    <div
      id="sidebar"
      onClickCapture={dnd.handleClickCapture}
      className="flex flex-col w-[168px] min-w-[168px] pt-3 pb-1 bg-blue-500 z-50 px-2"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <div>
          <Link to={userRole === "enduser" ? "/stats" : "/"}>
            <img
              className="object-scale-down hover:scale-110 cursor-pointer w-28 h-28 m-auto"
              src="https://s3.eu-west-3.amazonaws.com/storage.wixar.io/Wixar_Editor_Logo_Capital_500x500.png"
              alt="wixar-logo"
            />
          </Link>

          {canUseWixarAi && <AIChatButton />}
          {isBetaVersion && (
            <span className="text-white absolute text-m left-[60px] top-[80px]">Beta</span>
          )}
          <div className={!canUseWixarAi ? "mt-8" : ""}>{renderSections(false)}</div>
        </div>

        <div className="flex-1 min-h-[12px]" />

        <div className="flex flex-col">{renderSections(true)}</div>
      </div>

      <div className="flex flex-col shrink-0 mt-2">
        <div className="1536px:mb-4">
          <SidebarNavRow
            icon={LogoutIcon}
            iconClassName="stroke-red-500"
            labelClassName="text-red-500"
            title={t("sidebar.logout")}
            onClick={logout}
            to="/login"
          />
        </div>

        {legals && (
          <div className="flex flex-row justify-center mt-2">
            <span className="text-white flex flex-row items-center">
              <a
                className="underline text-xxs cursor-pointer"
                target="_blank"
                href={legals?.tos?.s3_url}
                rel="noopener noreferrer"
              >
                {t("footer.termsOfSales")}
              </a>
              <p className="text-xxs">&nbsp;|&nbsp;</p>
              <a
                className="underline text-xxs cursor-pointer"
                href={legals?.privacyPolicy?.s3_url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t("footer.privacyPolicy")}
              </a>
            </span>
          </div>
        )}
      </div>
      <p id={SIDEBAR_FAVORITES_DND_HELP_ID} className="sr-only">
        {t("sidebar.favorites.dndHelp")}
      </p>
      <div aria-live="polite" className="sr-only">
        {dnd.announcement}
      </div>
      <DeepLinkModal />
    </div>
  );
};

export default Sidebar;
